import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getLaborMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { LaborRegionBoxMetricsModel } from '@/types/clarity'
import { dateToString, getMappedRegionBranches } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useLaborRegionBoxMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<LaborRegionBoxMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    LaborRegionBoxMetricsModel[]
  >([])
  const { regionBranchMap, branches } = getMappedRegionBranches()
  const [processedData, setProcessedData] = useState<
    LaborRegionBoxMetricsModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics } = await fetchLaborRegionBoxMetrics(
          branches,
          startDate,
          endDate,
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics } = await fetchLaborRegionBoxMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to'),
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchTrendData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  const getProcessedData = useCallback(
    (
      currentData: LaborRegionBoxMetricsModel[],
      trendData: LaborRegionBoxMetricsModel[]
    ) => {
      if (!currentData.length || !trendData.length) return []

      const currentDataMap = new Map(
        currentData.map((item) => [item.stand, item])
      )
      const trendDataMap = new Map(trendData.map((item) => [item.stand, item]))
      const commonStands = [...currentDataMap.keys()].filter((stand) =>
        trendDataMap.has(stand)
      )

      const standsByRegion = commonStands.reduce((acc, stand) => {
        const region = currentDataMap.get(stand)?.region || ''
        const current = currentDataMap.get(stand)?.current
        const trend = trendDataMap.get(stand)?.current

        if (!acc.has(region)) {
          acc.set(region, [])
        }

        acc.get(region)?.push({
          stand: stand,
          region: region,
          current: {
            laborRatio: current?.laborRatio || 0,
            laborHours: current?.laborHours || 0,
            wagePerHour: current?.wagePerHour || 0,
            overtimePercentage: current?.overtimePercentage || 0,
          },
          prev: {
            laborRatio: trend?.laborRatio || 0,
            laborHours: trend?.laborHours || 0,
            wagePerHour: trend?.wagePerHour || 0,
            overtimePercentage: trend?.overtimePercentage || 0,
          },
        })

        return acc
      }, new Map<string, LaborRegionBoxMetricsModel[]>())

      const sortedStandsByRegion = new Map(
        [...standsByRegion.entries()].sort((a, b) => {
          const regionA = a[0].toLowerCase()
          const regionB = b[0].toLowerCase()
          return regionA.localeCompare(regionB)
        })
      )

      // Create parent regions with children stands
      const processedData = Array.from(sortedStandsByRegion.entries()).map(
        ([region, stands]) => {
          // Create parent region with aggregated metrics
          const regionData: LaborRegionBoxMetricsModel = {
            stand: region,
            current: {
              laborRatio:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.laborRatio || 0),
                  0
                ) / stands.length,
              laborHours:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.laborHours || 0),
                  0
                ) / stands.length,
              wagePerHour:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.wagePerHour || 0),
                  0
                ) / stands.length,
              overtimePercentage:
                stands.reduce(
                  (sum, stand) =>
                    sum + (stand.current?.overtimePercentage || 0),
                  0
                ) / stands.length,
            },
            prev: {
              laborRatio:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.laborRatio || 0),
                  0
                ) / stands.length,
              laborHours:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.laborHours || 0),
                  0
                ) / stands.length,
              wagePerHour:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.wagePerHour || 0),
                  0
                ) / stands.length,
              overtimePercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.overtimePercentage || 0),
                  0
                ) / stands.length,
            },
            children: stands,
          }

          return regionData
        }
      )

      setProcessedData(processedData as LaborRegionBoxMetricsModel[])
    },
    [branchData, branchTrendData]
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching labor region box data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (branchData.length && branchTrendData.length) {
      getProcessedData(branchData, branchTrendData)
    }
  }, [branchData, branchTrendData, getProcessedData])

  return {
    processedData,
    loading,
    error,
    refetch,
  }
}

async function fetchLaborRegionBoxMetrics(
  branches: string[],
  startDate: string,
  endDate: string,
  regionBranchMap: Map<string, string[]>
): Promise<{
  branchMetrics: LaborRegionBoxMetricsModel[]
}> {
  try {
    const response = await getLaborMetrics(
      branches.join(','),
      startDate,
      endDate
    )

    const metrics: LaborRegionBoxMetricsModel[] = response.map(
      (responseMetrics) =>
        setMetric(
          responseMetrics.branch_name,
          getBranchRegion(responseMetrics.branch_name, regionBranchMap),
          responseMetrics.total_labor_percentage,
          responseMetrics.total_hours,
          responseMetrics.unit_labor_cost,
          responseMetrics.overtime_percentage
        )
    )

    const branchMetrics = metrics

    return {
      branchMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function setMetric(
  stand: string,
  region: string,
  laborRatio: number,
  laborHours: number,
  wagePerHour: number,
  overtimePercentage: number
): LaborRegionBoxMetricsModel {
  return {
    stand: stand,
    region: region,
    current: {
      laborRatio: formatMetric(laborRatio, 1),
      laborHours: formatMetric(laborHours, 1),
      wagePerHour: formatMetric(wagePerHour, 2),
      overtimePercentage: formatMetric(overtimePercentage, 2),
    },
  }
}
